import React from 'react';
import ReactDOM from 'react-dom';
import Backbone from 'backbone';
import $ from 'jquery';
import _ from 'underscore';
import * as pgrid from 'projection-grid';
import { toolbarPlugin } from '@bingads-webui/projection-grid-toolbar';
import { recommendationInlineAction } from '@bingads-webui-campaign/recommendation-inline-action';
import { projectionGridEnableWithSelection } from '@bingads-webui-campaign/projection-grid-enable-with-selection';
import { crossPageSelectAllPlugin } from '@bingads-webui/projection-grid-cross-page-select-all';
import { assistedSelectionPlugin } from '@bingads-webui/projection-grid-assisted-selection';
import { paginationPlugin } from '@bingads-webui/projection-grid-pagination';
import { perfMarkerPlugin } from '@bingads-webui-campaign/pgrid-performance-log';
import { uiBlockerPlugin } from '@bingads-webui/projection-grid-uiblocker';
import { editablePlugin } from '@bingads-webui-campaign/projection-grid-editable';
import {
  CampaignContextualAttributeView,
  AdGroupContextualAttributeView,
  KeywordContextualAttributeView,
  AudienceContextualAttributeView,
  NegativeKeywordListContextualAttributeView,
  SharedBudgetContextualAttributeView,
  setPosition as setPositionOfContextualAttr,
} from '@bingads-webui-campaign/recommendation-contextual-attribute';
import { levelAtConstant } from '@bingads-webui-campaign/scope-constants';
import {
  VISUAL_TYPES,
  RECOMMENDATIOIN_SUMMARY_APPLY_FAILED_RESULTS,
  ADINSIGHT_LOG_API_NAME,
  CHANNEL_TYPES,
  RECOMMENDATION_IDS,
  ADINSIGHT_LOG_ACTION_TYPE,
  CheckDocBottomMargin,
  CONTEXTUAL_ENTITY_TYPE,
  DOWNLOAD_SCOPE,
} from '@bingads-webui-campaign/recommendation-core';
import { AdvisorChart } from '@bingads-webui-campaign-react/advisor-chart';
import { formatNotificationLocalizedKey } from '@bingads-webui-campaign/recommendation-notification';
import { RecommendationStatusLightbox } from '@bingads-webui-campaign/recommendation-status-lightbox';
import { getSessionStorage } from '@bingads-webui/storage';

import { getConfig } from './grid-config';
import { recommendationToolBarPlugin } from './grid-toolbar-plugin';
import { RejectButton } from './reject-button/grid-reject-button';
import { isSelectableType, isPopupEditorEnabledRecommendationType, getSearchTermChartData } from './const';
import template from './template.pug';
import { filterByOpportunityId, removeFilter } from './filter-helper';

export class RecommendationGrid extends Backbone.View {
  initialize({
    deps,
    i18n,
    newI18n,
    recommendationType,
    channel,
    recommendationService,
    downloadService,
    category,
    onEvent,
    context,
    guid,
    version,
    perfMarker,
    primaryEstimate,
    readOnly,
    compact,
    s$i18n,
  }) {
    if (perfMarker) {
      this.perfMarker = perfMarker.createChild('RecommendationDetailsGrid', { autoEnd: true });
      this.perfMarker.willInit();
    }
    this.deps = deps;
    this.i18n = i18n;
    this.newI18n = newI18n;
    this.s$i18n = s$i18n;
    this.recommendationType = recommendationType;
    this.channel = channel || CHANNEL_TYPES.RECOMMENDATION;
    this.recommendationService = recommendationService;
    this.downloadService = downloadService;
    this.onEvent = onEvent;
    this.timerForPopupContextualAttribute = null;
    this.popoverId = 'contextual-attribute';
    this.readOnly = readOnly;
    this.filterState = {};
    this.rejectButton = new RejectButton({
      deps,
      i18n,
      recommendationService,
      recommendationType,
      category,
      guid,
      callback: () => {
        this.onEvent('refreshSummary');
      },
      readOnly: this.readOnly,
    });
    this.context = context;
    this.guid = guid;
    this.version = version;
    this.primaryEstimate = primaryEstimate || '';
    this.compact = compact || false;

    const applyFailedResults = getSessionStorage().get(RECOMMENDATIOIN_SUMMARY_APPLY_FAILED_RESULTS); // eslint-disable-line
    const applyFailed = applyFailedResults !== null && applyFailedResults.recommendationType === this.recommendationType; // eslint-disable-line

    this.statusLightBox = new RecommendationStatusLightbox({
      results: applyFailed ? applyFailedResults.results : null,
      i18n,
      newI18n,
      view: this,
      filterByOpportunityId,
      removeFilter,
      recommendationType,
      formatNotificationLocalizedKey,
      currentActivity: this.recommendationService.currentActivity,
    });
    this.statusLightBox.render();

    if (applyFailed) {
      this.statusLightBox.trigger('show');
    } else {
      getSessionStorage().clear();
    }

    this.gridFactory = pgrid.factory({ vnext: true })
      // For accurate grid init latency, PerfMarkerPlugin should be the first plugin to load.
      .use(perfMarkerPlugin)
      .use(recommendationInlineAction)
      .use(toolbarPlugin)
      .use(assistedSelectionPlugin)
      .use(projectionGridEnableWithSelection)
      .use(uiBlockerPlugin);

    if (isSelectableType(recommendationType)) {
      this.gridFactory = this.gridFactory
        .use(paginationPlugin)
        .use(crossPageSelectAllPlugin)
        .use(recommendationToolBarPlugin);
    }

    if (isPopupEditorEnabledRecommendationType(recommendationType)) {
      this.gridFactory = this.gridFactory
        .use(editablePlugin);
    }
  }

  get events() {
    const contextualEvents = {
      'mouseover .grid-column-campaignName .contextual-tooltip': 'showContextualAttributePopup',
      'mouseout .grid-column-campaignName .contextual-tooltip': 'hideContextualAttributePopup',
      'mouseover .grid-column-adGroupName .contextual-tooltip': 'showContextualAttributePopup',
      'mouseout .grid-column-adGroupName .contextual-tooltip': 'hideContextualAttributePopup',
      'mouseover .grid-column-keyword .contextual-tooltip': 'showContextualAttributePopup',
      'mouseout .grid-column-keyword .contextual-tooltip': 'hideContextualAttributePopup',
      'mouseover .grid-column-BidAdjustmentTodo .contextual-tooltip': 'showContextualAttributePopup',
      'mouseout .grid-column-BidAdjustmentTodo .contextual-tooltip': 'hideContextualAttributePopup',
      'mouseover .grid-column-BudgetDelta .contextual-tooltip': 'showContextualAttributePopup',
      'mouseout .grid-column-BudgetDelta .contextual-tooltip': 'hideContextualAttributePopup',
      'mouseover .grid-column-blockedKeywordList .contextual-tooltip': 'showContextualAttributePopup',
      'mouseout .grid-column-blockedKeywordList .contextual-tooltip': 'hideContextualAttributePopup',
      'mouseover .grid-column-removeNKW .contextual-tooltip': 'showContextualAttributePopup',
      'mouseout .grid-column-removeNKW .contextual-tooltip': 'hideContextualAttributePopup',
      'click .grid-column-BudgetDelta .contextual-tooltip': 'router',
      'click .grid-column-BidAdjustmentTodo .contextual-tooltip': 'router',
      'click .grid-column-removeNKW .contextual-tooltip': 'router',
    };
    const otherEvents = {
      'click .more-tooltip .more-link': 'showClickableTooltip',
      'click .more-tooltip .close-button': 'hideClickableTooltip',
      'click .more-tooltip .more-top-search-term': 'showTopSearchTermChart',
      'change .match_type_inline_selection .match-type-select': 'updateMatchType',
      'click .grid-column-campaignName .contextual-tooltip': 'router',
      'click .grid-column-adGroupName .contextual-tooltip': 'router',
    };

    if (this.compact) {
      return otherEvents;
    }

    return {
      ...contextualEvents,
      ...otherEvents,
    };
  }

  router(e) {
    const target = $(e.target);
    // only campaign or adgroup contextual attribute can be clicked to redirect
    if (!_.contains([CONTEXTUAL_ENTITY_TYPE.CAMPAIGN, CONTEXTUAL_ENTITY_TYPE.ADGROUP], target.data('type'))) { // eslint-disable-line
      return;
    }
    const levelAt = target.data('type');
    const entityid = target.data('entityid');
    const campaignId = target.data('entitycampaignid');
    const { scope } = this.recommendationService;

    if (isPopupEditorEnabledRecommendationType(this.recommendationType)
      && levelAt === levelAtConstant.ADGROUP) {
      return;
    }
    if (levelAt !== scope.levelAt) {
      const scopedata = {
        ...scope,
        levelAt,
        adGroupId: entityid,
        campaignId: campaignId || entityid,
      };
      this.onEvent('updateScope', scopedata);
    }
  }

  hideContextualAttributePopup(e) {
    clearTimeout(this.timerForPopupContextualAttribute);
    const target = $(e.target);
    $(`.contextual-attribute-${target.data('opportunityid')}-${target.data('entityid')}`).hide();
  }

  showContextualAttributePopup(e) {
    clearTimeout(this.timerForPopupContextualAttribute);
    this.timerForPopupContextualAttribute = setTimeout(() => {
      const target = $(e.target);
      const contextual = $(`.contextual-attribute-${target.data('opportunityid')}-${target.data('entityid')}`);

      if (contextual.length === 0) {
        return;
      }
      if (!target.data('status')) {
        const contextualAttributeMapper = {
          [CONTEXTUAL_ENTITY_TYPE.CAMPAIGN]: CampaignContextualAttributeView,
          [CONTEXTUAL_ENTITY_TYPE.ADGROUP]: AdGroupContextualAttributeView,
          [CONTEXTUAL_ENTITY_TYPE.KEYWORD]: KeywordContextualAttributeView,
          [CONTEXTUAL_ENTITY_TYPE.AUDIENCE]: AudienceContextualAttributeView,
          [CONTEXTUAL_ENTITY_TYPE.NEGATIVE_KEYWORD_LIST]:
            NegativeKeywordListContextualAttributeView,
          [CONTEXTUAL_ENTITY_TYPE.SHARED_BUDGET]: SharedBudgetContextualAttributeView,
        };
        this.contextualAttributeView = new contextualAttributeMapper[target.data('type')]({
          deps: this.deps,
          i18n: this.i18n,
          targetId: Number(target.data('entityid')),
          el: contextual,
        });
        this.contextualAttributeView.render();
        target.data('status', 'true');
      }
      const container = contextual.children('.contextual-tooltip-container');
      contextual.show();
      if (container.length > 0) {
        setPositionOfContextualAttr(container);
      }
    }, 500);
  }

  showClickableTooltip(e) {
    $('.more-tooltip-container').hide();
    const target = $(e.target);
    const tooltipContainer = target.parent().find('.more-tooltip-container');
    tooltipContainer.show();

    // Add click listener to window
    $(window).on('click', (event) => {
      this.onClickGlobal(event);
    });
    tooltipContainer.css('top', '');
    tooltipContainer.css('bottom', '');
    tooltipContainer.css('margin-top', '');
    tooltipContainer.css('margin-bottom', '');
    if (!CheckDocBottomMargin(tooltipContainer, 50)) {
      tooltipContainer.css('bottom', '100%');
      // the width-expander layer's height is zero,
      // so we had to add the height of text to the margin
      tooltipContainer.css('margin-bottom', '17px');
    } else {
      tooltipContainer.css('top', '100%');
      tooltipContainer.css('margin-top', '5px');
    }
  }

  showTopSearchTermChart(e) {
    $('.more-tooltip-container').hide();
    const target = $(e.target);
    const tooltipContainer = target.parent().find('.more-tooltip-container');
    tooltipContainer.show();

    // Add click listener to window
    $(window).on('click', (event) => {
      this.onClickGlobal(event);
    });

    // Get searchTermList from data-searchtermlist
    const searchTermList = target.data('searchtermlist');
    let chart = null;
    const visualType = VISUAL_TYPES.SEARCH_TERMS_CHART;
    const visualData = getSearchTermChartData(searchTermList);
    chart = (<AdvisorChart
      i18n={this.i18n}
      type="RecommendationCard"
      subType={visualType}
      {...visualData}
    />);
    const container = target.parent().find('.more-top-search-term-content');
    if (container && container.length > 0) {
      ReactDOM.render(
        chart,
        container[0]
      );
    }
    tooltipContainer.css('top', '');
    tooltipContainer.css('bottom', '');
    tooltipContainer.css('margin-top', '');
    tooltipContainer.css('margin-bottom', '');
    if (!CheckDocBottomMargin(tooltipContainer, 50)) {
      tooltipContainer.css('bottom', '100%');
      tooltipContainer.css('margin-bottom', '5px');
    } else {
      tooltipContainer.css('top', '100%');
      tooltipContainer.css('margin-top', '5px');
    }
  }

  updateMatchType(e) {
    const key = $(e.target).parents('tr[data-key]').data('key');
    const recommendation = this.grid.gridView.itemWithKey(key);
    const index = e.target.selectedIndex;
    _.first(recommendation.Opportunities).NewMatchType = Number(e.target.options[index].value);

    // Append the current selected one into existing selected ones.
    const selectedKeys = this.grid.gridView.selectedKeys();
    if (!_.contains(selectedKeys, key)) {
      selectedKeys.push(key);
    }
    this.grid.gridView.set({ selection: _.defaults({ selected: selectedKeys }) });
  }

  hideClickableTooltip(e) {
    const target = $(e.target);
    target.parents('.more-tooltip-container').hide();

    // Remove click listener from window
    $(window).off('click');
  }

  onClickGlobal(e) {
    const target = $(e.target);
    if (target.parent().length > 0 &&
      target.parents('.more-tooltip-container').length === 0 &&
      !target.parent().hasClass('more-tooltip') &&
      !target.hasClass('more-tooltip-container')) {
      $('.more-tooltip-container').hide();

      // Remove click listener from window
      $(window).off('click');
    }
  }

  logOpportunityEvent = ({
    opportunityId = null,
    campaignId = null,
    input = null,
    type,
    action,
    context = {},
  }) => {
    this.recommendationService.currentActivity.trace({
      id: opportunityId,
      type,
      typeId: RECOMMENDATION_IDS[type],
      action,
      campaignId,
      channel: this.channel,
      context: action === ADINSIGHT_LOG_ACTION_TYPE.DOWNLOAD ?
        JSON.stringify(_.extend({
          Version: this.version,
          DownloadScope: DOWNLOAD_SCOPE.INDIVIDUAL,
        }, this.context)) :
        JSON.stringify(_.extend({}, this.context, context)),
      guid: this.guid,
      input,
    }, ADINSIGHT_LOG_API_NAME.RECOMMENDATION);
  }

  render() {
    this.$el.html(template());

    this.config = getConfig({
      deps: this.deps,
      i18n: this.i18n,
      newI18n: this.newI18n,
      s$i18n: this.s$i18n,
      recommendationType: this.recommendationType,
      channel: this.channel,
      recommendationService: this.recommendationService,
      el: this.$('.recommendation-grid'),
      downloadService: this.downloadService,
      onEvent: this.onEvent,
      view: this,
      logOpportunityEvent: this.logOpportunityEvent,
      perfMarker: this.perfMarker,
      context: this.context,
      guid: this.guid,
      primaryEstimate: this.primaryEstimate,
      router: this.router.bind(this),
      readOnly: this.readOnly,
      compact: this.compact,
      scope: this.recommendationService.scope,
      filterState: this.filterState,
    });

    this.grid = this.gridFactory.create(this.config);
    const $grid = this.grid.gridView.render().$el;
    this.$('.grid-table').append($grid);
    this.$('.upper-toolbar').append(this.grid.toolbar.gridToolbarGroup.toolbarWithName('toolbar').$el);
    if (!this.compact) {
      this.$('.reject-container').append(this.rejectButton.render().$el);
    }
    this.$('.notification-container').append(this.statusLightBox.$el);
    this.grid.gridView.once('didReload', () => {
      const { gridRecCount } = this.config.plugins.recommendationInlineAction.gridService;
      const gridDataVersion = this.config.plugins.recommendationInlineAction.gridService.version;
      const cardDataVersion = this.version;

      if (gridRecCount === 0) {
        this.recommendationService.currentActivity.trace(
          `no recommendation in detail page, type=${this.recommendationType}, GUID=${this.guid}`,
          'recommendation/noRecommendation'
        );
        this.onEvent('refreshSummary');
      } else if (gridDataVersion && cardDataVersion && gridDataVersion !== cardDataVersion) {
        this.recommendationService.currentActivity.trace(
          `inconsistent recommendation version between grid and summary card, type=${this.recommendationType}, GUID=${this.guid}, gridVersion=${gridDataVersion}, cardVersion=${cardDataVersion}`,
          'recommendation/inconsistentRecommendationVersion'
        );
      }
      this.onEvent('loaded');
    });
    this.grid.gridView.once('refreshSummary', () => {
      this.onEvent('refreshSummary');
    });
    return this;
  }
}
